import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Clock, ShieldCheck } from 'lucide-react';
import {
  getProfessionals,
  getLocations,
  createProfessional,
  updateProfessional,
  deleteProfessional,
} from '../services/api';
import AvailabilityModal from '../components/AvailabilityModal';
import CoveragesModal from '../components/CoveragesModal';
import './ListPage.css';

// M2/M3 (NER-1386): profesionales del negocio con CRUD contra los write endpoints
// (NER-1475). La sede se resuelve a nombre via GET /locations.
const EMPTY_FORM = { name: '', locationId: '' };

function ProfessionalsPage() {
  const { t } = useTranslation();
  const [professionals, setProfessionals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // form modal (create / edit)
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // availability (weekly schedule) modal
  const [scheduleTarget, setScheduleTarget] = useState(null);

  // coverages (obras sociales / convenios) modal — NER-1778
  const [coverageTarget, setCoverageTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profRes, locRes] = await Promise.all([getProfessionals(), getLocations()]);
      setProfessionals(profRes?.professionals || []);
      setLocations(locRes?.locations || []);
    } catch (err) {
      console.error('Error loading professionals:', err);
      setProfessionals([]);
      setError(t('professionals.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const locationName = useCallback(
    (locationId) => {
      if (!locationId) return null;
      const found = locations.find((l) => l.locationId === locationId);
      return found ? found.name : locationId;
    },
    [locations]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name || '', locationId: p.locationId || '' });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditing(null);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setFormError(t('professionals.nameRequired'));
      return;
    }
    setSaving(true);
    setFormError('');
    const payload = { name, locationId: form.locationId || null };
    try {
      if (editing) {
        await updateProfessional(editing.professionalId, payload);
      } else {
        await createProfessional(payload);
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (err) {
      console.error('Error saving professional:', err);
      setFormError(t('professionals.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteProfessional(deleteTarget.professionalId);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      console.error('Error deleting professional:', err);
      if (err?.response?.status === 409) {
        setDeleteError(t('professionals.deleteConflict'));
      } else {
        setDeleteError(t('professionals.deleteError'));
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="list-page">
      <div className="page-header">
        <h1>{t('professionals.title')}</h1>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <Plus size={16} />
          {t('professionals.new')}
        </button>
      </div>
      <p className="list-page-subtitle">{t('professionals.subtitle')}</p>

      {error && <div className="error-message">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : professionals.length === 0 ? (
          <div className="empty-state">{t('professionals.empty')}</div>
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('professionals.name')}</th>
                  <th>{t('professionals.location')}</th>
                  <th className="col-actions">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {professionals.map((p) => {
                  const loc = locationName(p.locationId);
                  return (
                    <tr key={p.professionalId}>
                      <td>{p.name}</td>
                      <td>
                        {loc ? (
                          <span className="list-tag">{loc}</span>
                        ) : (
                          <span className="list-muted">{t('professionals.noLocation')}</span>
                        )}
                      </td>
                      <td className="col-actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setScheduleTarget(p)}
                          aria-label={t('availability.title')}
                          title={t('availability.title')}
                        >
                          <Clock size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setCoverageTarget(p)}
                          aria-label={t('professionalCoverages.title')}
                          title={t('professionalCoverages.title')}
                        >
                          <ShieldCheck size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => openEdit(p)}
                          aria-label={t('common.edit')}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setDeleteTarget(p);
                            setDeleteError('');
                          }}
                          aria-label={t('common.delete')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? t('professionals.editTitle') : t('professionals.newTitle')}</h2>
            </div>
            <form onSubmit={submitForm}>
              <div className="modal-body">
                {formError && <div className="error-message">{formError}</div>}
                <div className="form-group">
                  <label htmlFor="prof-name">{t('professionals.name')} *</label>
                  <input
                    id="prof-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    maxLength={120}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="prof-loc">{t('professionals.location')}</label>
                  <select
                    id="prof-loc"
                    value={form.locationId}
                    onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}
                  >
                    <option value="">{t('professionals.noLocation')}</option>
                    {locations.map((l) => (
                      <option key={l.locationId} value={l.locationId}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={closeForm}
                  disabled={saving}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!deleting) setDeleteTarget(null);
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('professionals.deleteTitle')}</h2>
            </div>
            <div className="modal-body">
              {deleteError && <div className="error-message">{deleteError}</div>}
              <p>{t('professionals.deleteConfirm', { name: deleteTarget.name })}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? t('common.saving') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {scheduleTarget && (
        <AvailabilityModal professional={scheduleTarget} onClose={() => setScheduleTarget(null)} />
      )}

      {coverageTarget && (
        <CoveragesModal professional={coverageTarget} onClose={() => setCoverageTarget(null)} />
      )}
    </div>
  );
}

export default ProfessionalsPage;
